import BaseValidationService, * as baseValidationService from "@edaz/services/baseValidationService";
import { CSLicensePlate, CSLicensePlateOptions } from "@edaz/services/validators/CSLicensePlate";
import dateRange, { DateRangeOptions } from "@edaz/services/validators/dateRange";
import { equalWith, EqualWithOptions } from "@edaz/services/validators/equalWith";
import hasRequiredLength from "@edaz/services/validators/hasRequiredLength";
import { isAuthCode } from "@edaz/services/validators/isAuthCode";
import { isEmail, IsEmailOptions } from "@edaz/services/validators/isEmail";
import { isLicensePlate, IsLicensePlateOptions } from "@edaz/services/validators/isLicensePlate";
import { isOrganizationIdentifier } from "@edaz/services/validators/isOrganizationIdentifier";
import { isPhoneNumber, IsPhoneNumberOptions } from "@edaz/services/validators/isPhoneNumber";
import range, { RangeOptions } from "@edaz/services/validators/range";
import required from "@edaz/services/validators/required";
import { bound } from "@frui.ts/helpers/dist";
import { ITypedEntityValidationRules } from "@frui.ts/validation/dist";
import { get } from "mobx";
import { licensePlateChangeValidator, LicensePlateChangeValidatorOptions } from "validators/licensePlateChangeValidator";
import LocalizationService from "./localizationService";
import requiredIf, { RequiredIfOptions } from "@edaz/services/validators/requiredIf";
import { isBankAccountNumber, IsBankAccountNumberOptions } from "../validators/isBankAccountNumber";
import isLength, { IsLengthOptions } from "../validators/isLength";
import { prohibitedCharacters } from "validators/hasValidCharacters";

export type EntityValidationRules<TTarget> = ITypedEntityValidationRules<TTarget, ValidationRules<TTarget>>;

export interface EntityCondition<TEntity> {
  condition: (entity: TEntity) => boolean;
}

export interface ValidationRules<TEntity = any> {
  required: boolean | string;
  requiredIf: RequiredIfOptions;
  isEmail: boolean | IsEmailOptions;
  isAuthCode: boolean;
  equalWith: EqualWithOptions<TEntity>;
  isTrue: boolean;
  range: RangeOptions;
  isOrganizationIdentifier: boolean;
  isPhoneNumber: boolean | IsPhoneNumberOptions;
  isLicensePlate: boolean | IsLicensePlateOptions;
  isBankAccountNumber: IsBankAccountNumberOptions;
  isLength: IsLengthOptions;
  prohibitedCharacters: boolean;
  licensePlateChange: LicensePlateChangeValidatorOptions<TEntity>;
  dateRange: DateRangeOptions;
  CSLicensePlate: CSLicensePlateOptions;
  hasRequiredLength: RangeOptions;
  manualValidation: any;
}

export default class ValidationService extends BaseValidationService<ValidationRules> {
  private valueRegex = /%value%/g;
  private propertyNameRegex = /%propertyName%/g;
  private constraintRegex = /%param\[(\d+)\]%/g;

  constructor(private localizationService: LocalizationService) {
    super();
  }

  initializeValidators() {
    this.registerValidator("required", required);
    this.registerValidator("requiredIf", requiredIf);
    this.registerValidator("isEmail", isEmail);
    this.registerValidator("isLength", isLength);
    this.registerValidator("isAuthCode", isAuthCode);
    this.registerValidator("equalWith", equalWith);
    this.registerValidator("range", range);
    this.registerValidator("isOrganizationIdentifier", isOrganizationIdentifier);
    this.registerValidator("isPhoneNumber", isPhoneNumber);
    this.registerValidator("isLicensePlate", isLicensePlate);
    this.registerValidator("dateRange", dateRange);
    this.registerValidator("CSLicensePlate", CSLicensePlate);
    this.registerValidator("hasRequiredLength", hasRequiredLength);
    this.registerValidator("licensePlateChange", licensePlateChangeValidator);
    this.registerValidator("isBankAccountNumber", isBankAccountNumber);
    this.registerValidator("prohibitedCharacters", prohibitedCharacters);
    this.registerValidator("isTrue", (value: any) => value === true);
    this.validators.set("manualValidation", (value, propertyName, entity, params) => get(params.errors, propertyName));
  }

  @bound
  localizedMessage(validatorName: string, errors?: baseValidationService.IValidationError, customMessage?: string) {
    const messages = [];

    if (customMessage) {
      messages.push(this.localizationService.translate(`validators.customMessages.${customMessage}`));
    } else if (errors?.constraints) {
      for (const constraint in errors?.constraints) {
        messages.push(this.localizationService.translate(`validators.${validatorName}.${constraint}`));
      }
    } else {
      messages.push(this.localizationService.translate(`validators.${validatorName}`));
    }

    return messages
      .map((message: string) => {
        if (errors) {
          message = message.replace(this.valueRegex, errors.value);

          if (errors.target) {
            message = message.replace(
              this.propertyNameRegex,
              // @TODO Deal better with model name
              this.localizationService.translateAttribute("validators", errors.propertyName!)
            );
          }
          message = message.replace(this.constraintRegex, (match, number) => {
            if (errors.params && errors.params.length >= number) {
              return errors.params[number - 1];
            }
            return match;
          });
        }
        return message;
      })
      .join(" ");
  }
}
